import React from 'react';
import {
  FiAlertTriangle,
  FiCheck,
  FiLock,
  FiRotateCcw,
  FiSkipForward,
  FiSlash
} from 'react-icons/fi';
import WarehouseProductDisplay from '@/lib/shared-coms/WarehouseProductDisplay';
import {
  additionalStopHints,
  isPickLineDone,
  isPickLineLocked,
  pickLineAtStop,
  pickLineExpectation,
  pickLineProduct,
  pickLineScansUnits,
  pickLineStatusLabel,
  pickLineStop,
  pickLockedBadge,
  pickOtherStopHints,
  pickQuarantinedLabel,
  pickSkippedZoneNames,
  pickSkippedZonesLabel,
  pickUnitScanOpenStop,
  type PickLine,
  type PickWalkStop
} from '../../pickLineSteps';

type PickLineRowProps = {
  // Declared because this app's JSX typings carry no IntrinsicAttributes, so a list `key` on a
  // function component is otherwise a type error (React consumes it; it never reaches the props).
  key?: string;
  line: PickLine;
  /** The zone stop this row sends the picker to; null for a finished line or one with no stop left. */
  stop?: PickWalkStop | null;
  /** The line's other stops still to visit, in walk order. */
  otherStops?: readonly PickWalkStop[];
  /**
   * Whether this row carries the line-wide actions (mark short, the confirmed zones and their Undo)
   * — the line's first row, so a line in several zones does not offer them on every one.
   */
  showLineActions?: boolean;
  /** Rows only act for the claim holder; everyone else sees the list read-only. */
  interactive: boolean;
  /**
   * Tapping the row. A controlled line this user may not pick (`controlledLocked`) is not tappable
   * and offers no actions; a tap on it still reaches the page, which says why.
   */
  onOpen: (line: PickLine, stop: PickWalkStop | null) => void;
  onMarkShort: (line: PickLine) => void;
  /** Undo the line — or, with `zoneId`, only that confirmed zone of it. */
  onUndo: (line: PickLine, zoneId?: string | null) => void;
  /** Put every zone skipped as empty for this line back into its allocation. */
  onRestoreZones?: (line: PickLine) => void;
};

/** The stop badge above the product name: rank first, zone loud, the line's other stops quiet. */
function StopBadge({
  line,
  stop,
  otherStops
}: {
  line: PickLine;
  stop: PickWalkStop | null;
  otherStops: readonly PickWalkStop[];
}) {
  const badge = pickLineStop(line, stop);
  // A unit-scan line part-way through a zone: the rest of that zone's units are scanned there.
  const scanning = !stop && pickLineScansUnits(line) ? pickUnitScanOpenStop(line) : null;
  if (scanning?.zone) {
    return (
      <span className="pick-stop">
        <span className="pick-stop__zone">{scanning.zone.name}</span>
        <span className="pick-stop__part">
          Scanning: {scanning.scannedQuantity ?? 0} of {scanning.quantity} here
        </span>
      </span>
    );
  }
  if (badge.kind === 'none') {
    return (
      <span className="pick-stop pick-stop--none">
        {/* Part of the line is in the tote: it is the REST no zone has, skipped or not. */}
        {line.status === 'picking'
          ? 'No other zone has the rest'
          : pickSkippedZoneNames(line).length
          ? 'No other zone has it'
          : 'No stock found'}
      </span>
    );
  }
  // With the walk, the other zones are rows of their own — the hint says where on the walk they are.
  const hints = stop ? pickOtherStopHints(otherStops) : additionalStopHints(line);
  return (
    <span className="pick-stop">
      {badge.stopNumber !== null && (
        <span className="pick-stop__rank">Stop {badge.stopNumber}</span>
      )}
      <span className="pick-stop__zone">{badge.zoneName}</span>
      {badge.partLabel && <span className="pick-stop__part">{badge.partLabel}</span>}
      {hints.map((hint) => (
        <span className="pick-stop__others" key={hint}>
          {hint}
        </span>
      ))}
    </span>
  );
}

/**
 * One row of the Pick list. A row still to pick is one ZONE STOP of a line: where to go (stop,
 * zone, "zone 1 of 2", the line's other stops), what to take there (product, NDC / UPC, that zone's
 * quantity) and — for a serialized line — the expiration the unit must carry; tapping it opens the
 * line sheet at that zone. A line part-way through its zones lists the zones already picked, each
 * with its own Undo. A finished line shows its outcome and an Undo. A line whose zone(s) the picker
 * found empty says which, with an Undo skip. Stock quarantined for the line at Pick (Missing /
 * Damaged) is listed on the line's first row, done or not — the trail of what left the shelf records.
 * A controlled line this user may not pick is LOCKED: shown where the walk puts it — stop, zone,
 * quantity, outcome — with a lock badge, and nothing on it can be selected.
 * A unit-scan (controlled) line counts scans ("3 of 5 scanned"), its zones show units scanned of
 * their share, a row part-way through a zone resumes scanning there, and its short takes a reason.
 */
function PickLineRow({
  line,
  stop = null,
  otherStops = [],
  showLineActions = true,
  interactive,
  onOpen,
  onMarkShort,
  onUndo,
  onRestoreZones
}: PickLineRowProps) {
  const done = isPickLineDone(line);
  const locked = isPickLineLocked(line);
  // The claim holder acts on every line but a locked one.
  const actionable = interactive && !locked;
  const picking = line.status === 'picking';
  const expectation = pickLineExpectation(pickLineAtStop(line, stop));
  const statusLabel = pickLineStatusLabel(line);
  const skippedLabel = done || !showLineActions ? null : pickSkippedZonesLabel(line);
  const quarantinedLabel = showLineActions ? pickQuarantinedLabel(line) : null;
  const confirmedStops = (line.confirmedStops || []).filter(
    (confirmed) => !!confirmed.zone
  );
  // The zones already picked, each with its own Undo, for a line picked across zones (a one-zone
  // line keeps its single Undo). Only on the line's first row.
  const showZones =
    showLineActions &&
    confirmedStops.length > 0 &&
    (picking || confirmedStops.length > 1);
  const quantity = stop ? stop.quantity : line.quantity;
  const open = () => {
    if (interactive && !done) onOpen(line, stop);
  };
  const tappable = actionable && !done;
  const unitScan = pickLineScansUnits(line);

  return (
    <div
      className={[
        'pick-line',
        done && 'pick-line--done',
        line.status === 'short' && 'pick-line--short',
        picking && 'pick-line--picking',
        locked && 'pick-line--locked',
        tappable && 'pick-line--tappable'
      ]
        .filter(Boolean)
        .join(' ')}
      data-order-item-id={line.orderItemId}
      data-stop-key={stop?.key}
    >
      <div
        className="pick-line__main"
        role={tappable ? 'button' : undefined}
        tabIndex={tappable ? 0 : undefined}
        aria-disabled={locked && !done ? true : undefined}
        onClick={open}
      >
        <div className="pick-line__product">
          <WarehouseProductDisplay
            product={pickLineProduct(line)}
            // A finished line has nothing left to locate, and its zones may already be empty.
            prefix={
              done ? null : <StopBadge line={line} stop={stop} otherStops={otherStops} />
            }
          >
            {!done && expectation && (
              <span className="pick-line__expectation">
                {expectation.expiration && <>Exp {expectation.expiration}</>}
                {expectation.lot && <small> · Lot {expectation.lot}</small>}
              </span>
            )}
            {locked && (
              <span className="pick-line__badge pick-line__badge--locked">
                <FiLock aria-hidden /> {pickLockedBadge(line)}
              </span>
            )}
            {line.isShortDated && (
              <span className="pick-line__badge pick-line__badge--short-dated">
                SHORT DATED
              </span>
            )}
            {picking && showLineActions && statusLabel && (
              <span className="pick-line__progress">{statusLabel}</span>
            )}
            {skippedLabel && (
              <span className="pick-line__skipped">
                <FiSkipForward aria-hidden /> {skippedLabel}
              </span>
            )}
            {quarantinedLabel && (
              <span className="pick-line__skipped pick-line__quarantined">
                <FiAlertTriangle aria-hidden /> {quarantinedLabel}
              </span>
            )}
          </WarehouseProductDisplay>
        </div>
        <div className="pick-line__side">
          {done ? (
            <span
              className={[
                'pick-line__status',
                line.status === 'short'
                  ? 'pick-line__status--short'
                  : 'pick-line__status--picked'
              ].join(' ')}
            >
              {statusLabel}
            </span>
          ) : (
            <span className="pick-line__qty">
              <small>Qty</small>
              {quantity}
              {stop && quantity !== line.quantity && (
                <small className="pick-line__qty-of">of {line.quantity}</small>
              )}
            </span>
          )}
        </div>
      </div>
      {/* A locked line has no actions; only a picked one keeps its "picked from" note. */}
      {interactive && showLineActions && (!locked || (line.status === 'picked' && !showZones)) && (
        <div className="pick-line__actions">
          {done && !locked && (
            <button
              type="button"
              className="pick-line__action"
              onClick={() => onUndo(line)}
            >
              <FiRotateCcw aria-hidden />{' '}
              {line.status === 'short'
                ? unitScan
                  ? 'Undo short (keep scans)'
                  : 'Undo marked short'
                : 'Undo'}
            </button>
          )}
          {!done && !locked && (
            <button
              type="button"
              className="pick-line__action pick-line__action--short"
              onClick={() => onMarkShort(line)}
            >
              <FiSlash aria-hidden />{' '}
              {unitScan ? (
                'Record short pick'
              ) : picking ? (
                'Mark the rest short'
              ) : (
                <>Can&apos;t find it — mark short</>
              )}
            </button>
          )}
          {skippedLabel && onRestoreZones && !locked && (
            <button
              type="button"
              className="pick-line__action"
              onClick={() => onRestoreZones(line)}
            >
              <FiRotateCcw aria-hidden /> Undo skip
            </button>
          )}
          {line.status === 'picked' && !showZones && (
            <span className="pick-line__confirmed">
              <FiCheck aria-hidden />
              {line.confirmedZone?.name ? ` ${line.confirmedZone.name}` : ''}
              {line.confirmedByName ? ` · ${line.confirmedByName}` : ''}
            </span>
          )}
        </div>
      )}
      {showZones && (
        <ul className="pick-line__zones" aria-label="Zones picked">
          {confirmedStops.map((confirmed) => (
            <li key={confirmed.zone!.id} className="pick-line__zone">
              <FiCheck aria-hidden /> {confirmed.zone!.name} ·{' '}
              {unitScan
                ? `${confirmed.scannedQuantity ?? 0} of ${confirmed.quantity} scanned`
                : confirmed.quantity}
              {confirmed.short ? ' (all it had)' : ''}
              {confirmed.confirmedByName ? ` · ${confirmed.confirmedByName}` : ''}
              {actionable && line.status !== 'short' && (
                <button
                  type="button"
                  className="pick-line__zone-undo"
                  aria-label={`Undo ${confirmed.zone!.name}`}
                  onClick={() => onUndo(line, confirmed.zone!.id)}
                >
                  <FiRotateCcw aria-hidden /> Undo
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default PickLineRow;
